import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import {
  InsightsLabV2Favorite,
  InsightsLabV2Report,
  useCreateInsightsLabV2FavoriteMutation,
  useDeleteInsightsLabV2FavoriteMutation,
  useGetInsightsLabV2FavoritesQuery,
  useGetInsightsLabV2ReportsQuery,
} from 'api/insightsLabV2'
import Star from 'components/Icons/Star'
import Loading from 'components/Loading'
import ModalContainer from 'components/ModalContainer'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Nav, Tab } from 'react-bootstrap-five'
import RemoveFavoriteModal from './RemoveFavoriteModal'
import ReportBrowser from './ReportBrowser'
import ReportFrame from './ReportFrame'
import ReportPickerModal from './ReportPickerModal'
import WorkspaceSearch from './WorkspaceSearch'

const t = buildTranslate('insights_lab_v2')

// A tab is an independent instance keyed by `key` (not reportId) so the same
// report can be opened multiple times (e.g. with different filters).
export type OpenTab = {
  key: string
  reportId: number
  title: string
  initialFilters: Record<string, string>
  currentFilters: Record<string, string>
}

// Sort keys so two logically-equal filter sets serialize identically
// (URLSearchParams is insertion-order sensitive) — otherwise a favorite saved
// with the same filters in a different key order would fail to match its tab.
export const serializeFilters = (filters: Record<string, string>): string => {
  const params = new URLSearchParams()
  Object.keys(filters)
    .sort()
    .forEach(key => params.append(key, filters[key]))
  return params.toString()
}

// The open workspace (which reports, in what order, with which filters, and which
// is active) is persisted into the `s` query param as base64-encoded JSON so the
// page can be refreshed or shared. Compact keys keep the URL short:
//   { t: [{ r: <reportId>, f?: <filters> }], a: <activeIndex> }
const STATE_PARAM = 's'
type PersistedState = {
  t: Array<{ r: number; f?: Record<string, string> }>
  a: number
}

// Standard base64 emits `+` and `/`, which a URLSearchParams round-trip mangles
// (`+` decodes to a space), corrupting the payload on refresh/share. Convert to
// the URL-safe base64 alphabet (and strip `=` padding) on encode, and reverse it
// on decode so the two stay in lockstep.
const toUrlSafeBase64 = (base64: string): string =>
  base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

const fromUrlSafeBase64 = (urlSafe: string): string => {
  const restored = urlSafe.replace(/-/g, '+').replace(/_/g, '/')
  // atob tolerates missing padding in most browsers, but re-pad to keep it
  // standards-correct.
  const padding =
    restored.length % 4 === 0 ? '' : '='.repeat(4 - (restored.length % 4))
  return restored + padding
}

export const encodeWorkspaceState = (
  tabs: OpenTab[],
  activeKey: string
): string => {
  const payload: PersistedState = {
    t: tabs.map(tab => {
      const hasFilters = Object.keys(tab.currentFilters).length > 0
      return hasFilters
        ? { r: tab.reportId, f: tab.currentFilters }
        : { r: tab.reportId }
    }),
    a: Math.max(
      tabs.findIndex(tab => tab.key === activeKey),
      0
    ),
  }
  return toUrlSafeBase64(btoa(encodeURIComponent(JSON.stringify(payload))))
}

export const decodeWorkspaceState = (raw: string): PersistedState | null => {
  try {
    const parsed = JSON.parse(decodeURIComponent(atob(fromUrlSafeBase64(raw))))
    if (!parsed || !Array.isArray(parsed.t)) return null
    // Tolerate a missing/invalid active index — restore clamps it to a valid tab.
    const active = typeof parsed.a === 'number' && parsed.a >= 0 ? parsed.a : 0
    return { t: parsed.t, a: active }
  } catch {
    return null
  }
}

// The in-page multi-tab report workspace. Rendered below the Insights Lab header
// + banner when the `insights-lab-v2` flag is on.
const Workspace = (): JSX.Element => {
  const [css] = useStyletron()
  // Keyboard focus indicator for the in-tab star/close icon buttons (WCAG 2.4.7).
  // They reset the native button chrome, so without this they have no visible
  // focus ring when reached via the keyboard.
  const tabActionFocus = css({
    ':focus-visible': {
      outline: `2px solid ${palette.belizeHole}`,
      outlineOffset: '1px',
      borderRadius: '2px',
    },
  })
  const {
    data: reports = [],
    isLoading,
    isError: reportsError,
  } = useGetInsightsLabV2ReportsQuery()
  // Wait for favorites too: the inline browser's default tab depends on whether
  // the user has any, so we must not render it before favorites have resolved.
  const {
    data: favorites = [],
    isLoading: favoritesLoading,
    isError: favoritesError,
  } = useGetInsightsLabV2FavoritesQuery()
  const [createFavorite] = useCreateInsightsLabV2FavoriteMutation()
  const [deleteFavorite, { isLoading: isDeletingFavorite }] =
    useDeleteInsightsLabV2FavoriteMutation()

  const [openTabs, setOpenTabs] = useState<OpenTab[]>([])
  const [activeKey, setActiveKey] = useState<string>('')
  // Star/close icons reveal on the active tab, the hovered tab, or the
  // keyboard-focused tab (so they're reachable without a mouse).
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  const [focusedKey, setFocusedKey] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [favTabKey, setFavTabKey] = useState<string | null>(null)
  const [favName, setFavName] = useState('')
  const [favDescription, setFavDescription] = useState('')
  const [savingFavorite, setSavingFavorite] = useState(false)
  // Tab whose star was clicked while already favorited — shows the remove
  // confirmation modal rather than deleting immediately.
  const [removeConfirmTabKey, setRemoveConfirmTabKey] = useState<string | null>(
    null
  )

  const tabCounter = useRef(0)
  const handledSearch = useRef<string | null>(null)
  // Becomes true once tabs have actually rendered, so the outbound effect won't
  // clobber an inbound ?s before the restore populates tabs.
  const hasShownTabs = useRef(false)

  // Always opens a NEW tab instance (the same report may be open many times).
  const openReport = useCallback(
    (
      reportId: number,
      title: string,
      filters: Record<string, string>
    ): void => {
      const key = `tab-${tabCounter.current++}`
      setOpenTabs(tabs => [
        ...tabs,
        {
          key,
          reportId,
          title,
          initialFilters: filters,
          currentFilters: filters,
        },
      ])
      setActiveKey(key)
      setSearchTerm('')
      setPickerOpen(false)
    },
    []
  )

  const handleOpenReport = useCallback(
    (report: InsightsLabV2Report): void =>
      openReport(report.id, report.title, {}),
    [openReport]
  )

  // A favorite opens with its custom name as the tab label.
  const handleOpenFavorite = useCallback(
    (favorite: InsightsLabV2Favorite): void =>
      openReport(favorite.lookerReportId, favorite.name, favorite.filterState),
    [openReport]
  )

  const restoreFromState = useCallback(
    (state: PersistedState): void => {
      const tabs = state.t
        // Only restore reports that exist in this merchant's catalog — never
        // trust a reportId decoded from the URL enough to open a tab (and fire
        // a signed-embed request) for an arbitrary id.
        .map(entry => ({ entry, report: reports.find(r => r.id === entry.r) }))
        .filter(({ report }) => report)
        .map(({ entry, report }) => {
          const filters = entry.f ?? {}
          // The persisted state only stores reportId + filters, not which
          // favorite (if any) opened the tab — so re-derive it here. Without
          // this, restoring a favorite (e.g. on refresh, or a shared link)
          // falls back to the underlying report's title instead of the
          // favorite's own custom name.
          const favorite = favorites.find(
            item =>
              item.lookerReportId === entry.r &&
              serializeFilters(item.filterState) === serializeFilters(filters)
          )
          return {
            key: `tab-${tabCounter.current++}`,
            reportId: entry.r,
            title: favorite?.name ?? report?.title ?? '',
            initialFilters: filters,
            currentFilters: filters,
          }
        })
      setOpenTabs(tabs)
      // This replaces the entire tab list with freshly-keyed tabs — any dialog
      // still referencing an old key would otherwise be left open pointing at
      // a tab that no longer exists (blank favorite name, a Remove that does
      // nothing).
      setFavTabKey(null)
      setRemoveConfirmTabKey(null)
      const activeIndex = Math.min(Math.max(state.a, 0), tabs.length - 1)
      setActiveKey(tabs[activeIndex]?.key ?? '')
    },
    [reports, favorites]
  )

  // INBOUND (URL -> state): restore the workspace from ?s, or open a single
  // report/favorite from the Customer-nav deep-links. Runs on mount and when
  // the query string changes via navigation; ignores our own outbound writes
  // (tracked in handledSearch) and waits until the catalog/favorites load.
  const search = window.location.search

  // Which sidebar section the inline ReportBrowser (below, when no tabs are
  // open) should land on — sourced from the Customer nav's "+N more"
  // overflow links (see Content.tsx's `overflowLink`s), which is the only
  // thing this param controls. Deliberately separate from the `s=`/
  // `favorite=`/`open=` handling above: those restore/open specific
  // workspace state, this only picks a sidebar section within the browsing
  // view. Read directly (not via state/effect) since ReportBrowser only
  // consumes it once, as its `selected` state's initial value.
  const sectionParam = new URLSearchParams(search).get('section')
  const initialSection: 'recommended' | 'favorites' | undefined =
    sectionParam === 'recommended' || sectionParam === 'favorites'
      ? sectionParam
      : undefined

  useEffect(() => {
    if (search === handledSearch.current) return
    const params = new URLSearchParams(search)
    const encoded = params.get(STATE_PARAM)
    const favId = params.get('favorite')
    const openId = params.get('open')

    // Gate on the loading flags, not `.length === 0` — an empty result is a
    // loaded state (nothing to open), not a "still loading" one, so a genuinely
    // empty catalog/favorites list shouldn't wedge the deep-link effect.
    if (encoded) {
      // Needs BOTH: reports to validate tab reportIds, favorites to derive tab
      // titles. Gating on isLoading alone raced favorites — when reports
      // resolved first, restoreFromState ran against an empty favorites list
      // (falling back to the report title for every tab), then marked this
      // search as handled before favorites arrived, so the correct titles
      // never got a second chance to apply.
      if (isLoading || favoritesLoading) return
      const state = decodeWorkspaceState(encoded)
      handledSearch.current = search
      if (state) restoreFromState(state)
    } else if (favId) {
      if (favoritesLoading) return
      handledSearch.current = search
      const favorite = favorites.find(item => item.id === Number(favId))
      if (favorite) handleOpenFavorite(favorite)
    } else if (openId) {
      if (isLoading) return
      handledSearch.current = search
      const report = reports.find(item => item.id === Number(openId))
      if (report) handleOpenReport(report)
    } else {
      handledSearch.current = search
      // A bare `/insights_lab` visit ("Browse all reports") or a `?section=`
      // link is a request to browse -- the inline ReportBrowser is only
      // rendered when openTabs is empty, so with tabs open this branch
      // otherwise did nothing visible while the navigation had already
      // overwritten the `?s=` URL representing those tabs (which the
      // outbound effect below, keyed only on [openTabs, activeKey], would
      // never notice and restore). Close the tabs so the click actually
      // does something, and so the outbound effect's own empty-openTabs
      // write is what settles the URL, not a stale `?s=`.
      if (openTabs.length > 0) setOpenTabs([])
    }
    // restoreFromState/handleOpen* are useCallback-stable (keyed on reports);
    // include the loading flags so the effect re-runs when a query resolves to
    // an empty result (where reports/favorites stay []).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, reports, favorites, isLoading, favoritesLoading, openTabs.length])

  // OUTBOUND (state -> URL): keep ?s in sync (via replaceState, no history spam)
  // so refresh/share restores the exact workspace. Mark the write as handled so
  // the inbound effect doesn't reprocess it.
  useEffect(() => {
    // Wait until the inbound effect has processed the incoming URL, so we don't
    // clear a shared ?s before it's been restored.
    if (handledSearch.current === null) return
    if (openTabs.length) hasShownTabs.current = true
    // Don't write an empty URL before the inbound restore has populated tabs;
    // only clear once tabs have actually been shown (covers user-closed-all).
    if (openTabs.length === 0 && !hasShownTabs.current) return
    const newSearch = openTabs.length
      ? `?${STATE_PARAM}=${encodeWorkspaceState(openTabs, activeKey)}`
      : ''
    if (window.location.search !== newSearch) {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${newSearch}`
      )
    }
    handledSearch.current = newSearch
  }, [openTabs, activeKey])

  const closeTab = useCallback(
    (key: string): void => {
      // Compute the fallback outside the updater — calling setActiveKey inside a
      // setOpenTabs updater is a side effect React 18 may run twice.
      const remaining = openTabs.filter(tab => tab.key !== key)
      setOpenTabs(remaining)
      // Closing the tab whose save-favorite (or remove-favorite) dialog is open
      // would otherwise leave that dialog open with a stale target — clear it.
      if (favTabKey === key) setFavTabKey(null)
      if (removeConfirmTabKey === key) setRemoveConfirmTabKey(null)
      if (activeKey === key) {
        const fallback = remaining[remaining.length - 1]
        setActiveKey(fallback ? fallback.key : '')
      }
    },
    [openTabs, activeKey, favTabKey, removeConfirmTabKey]
  )

  const recordFilters = useCallback(
    (key: string, filters: Record<string, string>): void => {
      setOpenTabs(tabs =>
        tabs.map(tab =>
          tab.key === key ? { ...tab, currentFilters: filters } : tab
        )
      )
    },
    []
  )

  // Index favorites by reportId + serialized filters once per favorites change,
  // so the per-tab lookup in render is O(1) instead of an O(favorites) scan.
  const favoriteByKey = useMemo(() => {
    const map = new Map<string, InsightsLabV2Favorite>()
    favorites.forEach(favorite =>
      map.set(
        `${favorite.lookerReportId}|${serializeFilters(favorite.filterState)}`,
        favorite
      )
    )
    return map
  }, [favorites])

  // The favorite (if any) matching a tab's report + current filter state.
  const favoriteFor = useCallback(
    (tab: OpenTab): InsightsLabV2Favorite | undefined =>
      favoriteByKey.get(
        `${tab.reportId}|${serializeFilters(tab.currentFilters)}`
      ),
    [favoriteByKey]
  )

  // Star click: confirm before unfavoriting if already saved, else open the
  // save dialog to name it.
  const toggleFavorite = (tab: OpenTab): void => {
    const existing = favoriteFor(tab)
    if (existing) {
      setRemoveConfirmTabKey(tab.key)
      return
    }
    setFavTabKey(tab.key)
    setFavName(tab.title)
    setFavDescription('')
  }

  const favTab = openTabs.find(tab => tab.key === favTabKey)

  const handleSaveFavorite = async (): Promise<void> => {
    // Guard against double-submit (Enter repeat / double-click) creating dupes.
    if (!favTab || !favName.trim() || savingFavorite) return
    setSavingFavorite(true)
    const result = await createFavorite({
      lookerReportId: favTab.reportId,
      name: favName.trim(),
      description: favDescription.trim() || undefined,
      filterState: favTab.currentFilters,
    })
    setSavingFavorite(false)
    // Keep the dialog open if the mutation failed, so the user can retry instead
    // of silently losing the favorite they just named.
    if ('error' in result) return
    // The star updates on its own (favoriteFor reacts to the invalidated
    // favorites cache), but the tab's own title is separate, static state —
    // update it here or the tab keeps showing the report title after saving.
    const savedName = result.data.name
    setOpenTabs(tabs =>
      tabs.map(tab =>
        tab.key === favTabKey ? { ...tab, title: savedName } : tab
      )
    )
    setFavTabKey(null)
    setFavName('')
    setFavDescription('')
  }

  const removeConfirmTab = openTabs.find(tab => tab.key === removeConfirmTabKey)
  const removeConfirmFavorite =
    removeConfirmTab && favoriteFor(removeConfirmTab)

  const handleConfirmRemoveFavorite = async (): Promise<void> => {
    if (!removeConfirmFavorite || isDeletingFavorite) return
    try {
      // Catch a rejected delete so it doesn't surface as an unhandled promise
      // rejection; on failure RTK skips tag invalidation, so the star simply
      // stays filled (no phantom optimistic toggle to roll back) and the
      // confirmation stays open so the user can retry.
      await deleteFavorite(removeConfirmFavorite.id).unwrap()
      setRemoveConfirmTabKey(null)
    } catch {
      // Left open intentionally — see comment above.
    }
  }

  if (isLoading || favoritesLoading) return <Loading />

  // Don't silently render an empty browser when the catalog/favorites failed to
  // load — that reads as "no reports" rather than "something went wrong".
  if (reportsError || favoritesError) {
    return (
      <div className="mt-xl" data-testid="insights-lab-v2-error">
        <Text.BodyText3 color="grey70">{t('load_error')}</Text.BodyText3>
      </div>
    )
  }

  return (
    <div className="mt-xl" data-testid="insights-lab-v2-workspace">
      {openTabs.length === 0 ? (
        // No report open → the page IS the report browser.
        <ReportBrowser
          reports={reports}
          favorites={favorites}
          onOpenReport={handleOpenReport}
          onOpenFavorite={handleOpenFavorite}
          onDeleteFavorite={id => deleteFavorite(id)}
          bodyHeight="65vh"
          initialSection={initialSection}
        />
      ) : (
        <>
          <WorkspaceSearch
            reports={reports}
            favorites={favorites}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onOpenReport={handleOpenReport}
            onOpenFavorite={handleOpenFavorite}
            onOpenPicker={() => setPickerOpen(true)}
          />

          <Tab.Container
            activeKey={activeKey}
            transition={false}
            onSelect={key => key && setActiveKey(key)}
          >
            <div className="border-bottom-1 grey-20-border">
              {/* mb-0 kills the default <ul> bottom margin (Bootstrap reboot)
                  that otherwise leaves a gap between the tabs and the report. */}
              <Nav className="nav-tabs border-none mb-0">
                {openTabs.map(tab => {
                  const isFavorited = Boolean(favoriteFor(tab))
                  const isActive = tab.key === activeKey
                  const isHovered = hoveredKey === tab.key
                  const isFocused = focusedKey === tab.key
                  // Default tab shows only its name; the star + close reveal on
                  // hover, keyboard focus, or when the tab is the active one.
                  const showActions = isActive || isHovered || isFocused

                  let background = 'transparent'
                  if (isActive) background = palette.belizeHole10
                  else if (isHovered) background = palette.grey10

                  return (
                    <Nav.Item key={tab.key}>
                      <Nav.Link
                        eventKey={tab.key}
                        className="d-flex align-items-center"
                        onMouseEnter={() => setHoveredKey(tab.key)}
                        onMouseLeave={() => setHoveredKey(null)}
                        // Reveal star/close when focus enters the tab (keyboard);
                        // clear it when focus leaves the whole tab subtree.
                        onFocus={() => setFocusedKey(tab.key)}
                        onBlur={event => {
                          if (
                            !event.currentTarget.contains(
                              event.relatedTarget as Node
                            )
                          ) {
                            setFocusedKey(current =>
                              current === tab.key ? null : current
                            )
                          }
                        }}
                        // Inline styles to override the global `.nav-tabs
                        // .nav-link[.active]` rules, which win on specificity.
                        style={{
                          gap: '4px',
                          padding: '12px 8px',
                          border: 'none',
                          borderTopLeftRadius: isActive ? '4px' : 0,
                          borderTopRightRadius: isActive ? '4px' : 0,
                          backgroundColor: background,
                          // Blue underline on the active tab; transparent
                          // elsewhere so tabs don't shift height.
                          borderBottom: isActive
                            ? `2px solid ${palette.belizeHole}`
                            : '2px solid transparent',
                          boxShadow: isActive
                            ? '0px 1px 2px rgba(54, 66, 78, 0.3)'
                            : 'none',
                        }}
                      >
                        {/* Star + close are always in the DOM so the tab keeps
                            a constant width — toggling `visibility` reveals them
                            on hover/active without shifting the row. */}
                        <button
                          type="button"
                          className={`border-0 bg-transparent p-0 ${tabActionFocus}`}
                          aria-label={
                            isFavorited
                              ? t('remove_favorite', { title: tab.title })
                              : t('add_favorite', { title: tab.title })
                          }
                          aria-hidden={!showActions}
                          tabIndex={showActions ? 0 : -1}
                          style={{
                            visibility: showActions ? 'visible' : 'hidden',
                          }}
                          onClick={event => {
                            // Inside the Nav.Link <a href="#"> — prevent the
                            // anchor from navigating, not just bubbling.
                            event.preventDefault()
                            event.stopPropagation()
                            toggleFavorite(tab)
                          }}
                        >
                          <Star
                            fillColor={isFavorited ? palette.eggYolk50 : 'none'}
                            strokeColor={palette.grey70}
                          />
                        </button>
                        <Text.BodyText5
                          color={showActions ? 'grey90' : 'grey70'}
                          bold={isActive}
                        >
                          {tab.title}
                        </Text.BodyText5>
                        <button
                          type="button"
                          className={`border-0 bg-transparent p-0 ${tabActionFocus}`}
                          aria-label={t('close_tab', { title: tab.title })}
                          aria-hidden={!showActions}
                          tabIndex={showActions ? 0 : -1}
                          style={{
                            visibility: showActions ? 'visible' : 'hidden',
                          }}
                          onClick={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            closeTab(tab.key)
                          }}
                        >
                          <i
                            aria-hidden="true"
                            className={`fa fa-times ${css({
                              fontSize: '12px',
                              color: palette.grey70,
                            })}`}
                          />
                        </button>
                      </Nav.Link>
                    </Nav.Item>
                  )
                })}
                {/* Trailing "+" opens the report picker, mirroring the
                    "All reports" button — a quick way to add another tab. */}
                <Nav.Item className="d-flex align-items-center">
                  <button
                    type="button"
                    className={`border-0 bg-transparent d-flex align-items-center justify-content-center ${css(
                      {
                        padding: '12px 14px',
                        color: palette.grey70,
                        lineHeight: 1,
                      }
                    )}`}
                    aria-label={t('open_report')}
                    onClick={() => setPickerOpen(true)}
                  >
                    {/* Inline thin "+" — the FontAwesome glyph reads too heavy
                        next to the tab labels, so draw a 1.5px-stroke plus. */}
                    <svg
                      aria-hidden="true"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M7 1.75v10.5M1.75 7h10.5"
                        stroke={palette.grey70}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </Nav.Item>
              </Nav>
            </div>
            <Tab.Content>
              {openTabs.map(tab => (
                <Tab.Pane key={tab.key} eventKey={tab.key} mountOnEnter>
                  <ReportFrame
                    reportId={tab.reportId}
                    reportName={tab.title}
                    initialFilters={tab.initialFilters}
                    onFilterChange={filters => recordFilters(tab.key, filters)}
                  />
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Tab.Container>
        </>
      )}

      <ReportPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        reports={reports}
        favorites={favorites}
        onOpenReport={handleOpenReport}
        onOpenFavorite={handleOpenFavorite}
        onDeleteFavorite={id => deleteFavorite(id)}
      />

      <RemoveFavoriteModal
        isOpen={removeConfirmTabKey !== null}
        favoriteName={removeConfirmFavorite?.name ?? ''}
        isRemoving={isDeletingFavorite}
        onClose={() => setRemoveConfirmTabKey(null)}
        onRemove={handleConfirmRemoveFavorite}
      />

      <ModalContainer
        isOpen={favTabKey !== null}
        onClose={() => setFavTabKey(null)}
        title={t('save_favorite')}
        proceedText={t('save')}
        canProceed={favName.trim().length > 0 && !savingFavorite}
        onProceed={handleSaveFavorite}
      >
        <label htmlFor="il2-favorite-name" className="d-block mb-s w-100">
          <Text.BodyText5 bold tag="div">
            {t('favorite_name')}
          </Text.BodyText5>
          <input
            id="il2-favorite-name"
            className="form-control"
            required
            aria-required="true"
            value={favName}
            onChange={event => setFavName(event.target.value)}
          />
        </label>
        <label htmlFor="il2-favorite-description" className="d-block w-100">
          <Text.BodyText5 bold tag="div">
            {t('favorite_description')}
          </Text.BodyText5>
          <input
            id="il2-favorite-description"
            className="form-control"
            value={favDescription}
            onChange={event => setFavDescription(event.target.value)}
          />
        </label>
      </ModalContainer>
    </div>
  )
}

export default Workspace
